import React, { createContext, useContext, useEffect, useState } from "react";

import { FileEntity, Track } from "@highnote/server/src/core/entities";
import { useTrackFiles } from "App/components/useEntities/useTrack";
import { ViewingSpaceId } from "App/modules/viewingSpace";
import { EntitiesSelectionProvider } from "App/components/useEntitiesSelection";
import { FileEditorProvider } from "App/components/useFileEditor";
import { useGlobalTracks } from "App/store/tracks/useGlobalTracks";
import { RenderableTrack } from "App/store/tracks/buildSpaceTrackTile";
import { WatcherComponentId } from "App/store/watchers/useWatcherV2";
import isEqual from "lodash/isEqual";
import { TrackVersionEditor } from "../../TrackVersionEditor";
import { useVersionRows } from "../hooks/useVersionRows";
import { TrackVersionSelectionProvider } from "./TrackVersionSelectionContext";

// Shared by every site that attaches or reads the track-version-editor
// watcher's loading state: this file's track watcher attach (below),
// TrackVersionEditor.tsx's space watcher attach, and SpaceTrack.tsx's read
// of `loading.get(...)` for the "not found" check. All three are valid
// WatcherComponentId strings on their own, so nothing but this shared
// constant keeps a typo'd literal in any one of them from silently
// desyncing from the others.
export const TRACK_VERSION_EDITOR_WATCHER_COMPONENT_ID: WatcherComponentId =
  "track-version-editor";

type TrackVersionEditorContext = ReturnType<typeof useVersionRows> & {
  localTrackVersions: FileEntity[];
  setLocalTrackVersions: React.Dispatch<React.SetStateAction<FileEntity[]>>;
  localPinnedTrackVersions: string[];
  setLocalPinnedTrackVersions: React.Dispatch<React.SetStateAction<string[]>>;
  openTrackVersionEditor: (config: TrackVersionEditorConfig) => void;
  closeTrackVersionEditor: () => void;
  isTrackVersionEditorOpen: boolean;
  trackVersionEditorType: TrackVersionEditorType;
  selectedVersionToSwap: FileEntity | undefined;
  setSelectedVersionToSwap: React.Dispatch<
    React.SetStateAction<FileEntity | undefined>
  >;
  /**
   * Point the editor at a track, and record the space it is being viewed
   * FROM. This provider is mounted once (Main.tsx) and outlives every
   * route, so it cannot read the route itself: the player opens it with
   * whatever is playing, wherever the user is standing.
   *
   * Pass `undefined` to CLEAR the editor: the `trackId` effect below
   * resets it and detaches the watcher. The borrowed track page uses this
   * to drop a canonical id it must not keep watching.
   */
  setTrackId: (
    trackId: Track["id"] | undefined,
    viewingSpaceId?: ViewingSpaceId,
  ) => void;
  /** The space the current track is being viewed from, if known. */
  viewingSpaceId: ViewingSpaceId | undefined;
  trackVersionsLoading: boolean;
};

const TrackVersionEditorContext =
  createContext<TrackVersionEditorContext>(null);

export type TrackVersionEditorType = "default" | "swap" | "pin";

type BaseTrackVersionEditorConfig = {
  /**
   * `RenderableTrack`, not `Track`: a BORROWED track is a tile built from a
   * `space-tracks-v1` row alone, so it legitimately has no canonical
   * `versionFilesV2`/`files`/`spaceId`. See `buildSpaceTrackTile.ts`, whose
   * own doc says to prefer this type on any signature it can reach.
   */
  track: RenderableTrack;
  /**
   * REQUIRED, and that is the point. Is the canonical `tracks-v2` document
   * out of this viewer's reach?
   *
   * This used to be read inside the provider as
   * `(config.track as SpaceTrackTile).isBorrowed`. A cast reads a field off
   * a type that need not have it, so a call site handing over a plain
   * `Track` produced `undefined` in silence, the provider read that as "not
   * borrowed", and it attached a live `tracks-v2` watcher that a borrower is
   * denied. Only the caller knows which space it is standing in, so the
   * compiler now asks every one of them.
   *
   * A sibling of `track` rather than a field ON it: requiring it on the
   * track would force every call site to build a new object
   * (`{ ...track, isBorrowed }`), and that object — not the tile the space
   * page holds — is what would go into the global track store.
   *
   * `SpaceTrackTile` (from `buildSpaceTrackTile`) carries the answer; pass
   * `track.isBorrowed === true` where the value is a tile, `false` where the
   * track can only have come from the canonical document.
   */
  isBorrowed: boolean;
  /** The space the track is being opened FROM — see `setTrackId`. */
  viewingSpaceId?: ViewingSpaceId;
};

type DefaultTrackVersionEditorConfig = BaseTrackVersionEditorConfig & {
  type: "default";
};

type PinTrackVersionEditorConfig = BaseTrackVersionEditorConfig & {
  type: "pin";
};

type SwapTrackVersionEditorConfig = BaseTrackVersionEditorConfig & {
  type: "swap";
  selectedVersionToSwap: FileEntity;
};

type TrackVersionEditorConfig =
  | DefaultTrackVersionEditorConfig
  | PinTrackVersionEditorConfig
  | SwapTrackVersionEditorConfig;

export const TrackVersionEditorProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [trackId, _setTrackId] = useState<Track["id"]>(undefined);
  // The id of the track the editor was opened on, recorded ONLY when that
  // track was BORROWED (a tile whose `space-tracks-v1` row lives in a space
  // that is not the track's origin). Kept as an id rather than a flag so
  // that a later `setTrackId` for a different track cannot inherit it.
  //
  // Borrowed, NOT merely row-backed: a space's own tracks have an origin row
  // too, and their canonical document is perfectly readable. Keying this on
  // `isRowBacked` dropped the live watcher for every one of them.
  const [borrowedTrackId, setBorrowedTrackId] =
    useState<Track["id"]>(undefined);
  const [viewingSpaceId, setViewingSpaceId] = useState<
    ViewingSpaceId | undefined
  >(undefined);
  const { globalTracks, manageGlobalTrackWatchers, globalTracksDispatch } =
    useGlobalTracks();
  const globalTrack = globalTracks.tracks.get(trackId);
  const isBorrowedTrack = Boolean(trackId) && borrowedTrackId === trackId;
  // A borrowed track has no canonical document this viewer may read, so
  // GET_TRACK would deny it. Its files come from the ids the row mirrors.
  const { versions: trackVersions, loading: trackVersionsLoading } =
    useTrackFiles(globalTrack, viewingSpaceId, isBorrowedTrack);

  const [isTrackVersionEditorOpen, setTrackVersionEditorOpen] = useState(false);

  const [trackVersionEditorType, setTrackVersionEditorType] =
    useState<TrackVersionEditorType>("default");
  const [selectedVersionToSwap, setSelectedVersionToSwap] = useState<
    FileEntity | undefined
  >(undefined);

  const [localTrackVersions, setLocalTrackVersions] = useState(
    trackVersions || [],
  );
  const [localPinnedTrackVersions, setLocalPinnedTrackVersions] = useState(
    globalTrack?.pinnedVersionFiles || [],
  );

  const versionRows = useVersionRows({
    track: globalTrack,
    trackVersions: localTrackVersions,
    pinnedTrackVersions: localPinnedTrackVersions,
  });

  const resetTrackVersionEditor = () => {
    setLocalTrackVersions([]);
    setLocalPinnedTrackVersions([]);
    _setTrackId(undefined);
    setBorrowedTrackId(undefined);
    setViewingSpaceId(undefined);
    setTrackVersionEditorOpen(false);
  };

  const setTrackId = (
    trackId: Track["id"] | undefined,
    nextViewingSpaceId?: ViewingSpaceId,
  ) => {
    _setTrackId(trackId);
    // A borrowed id recorded for a PREVIOUS track must not survive to gate
    // this one. Without this, closing the editor on a borrowed track and
    // then pointing it at that SAME track id in its own origin space (e.g.
    // SpaceTrack.tsx navigating there) leaves `trackId` unchanged, so the
    // `trackId` effect never re-runs and the canonical watcher never
    // attaches. `openTrackVersionEditor` calls this first and then sets
    // `borrowedTrackId` explicitly afterward, so that ordering still wins.
    setBorrowedTrackId(undefined);
    setViewingSpaceId(nextViewingSpaceId);
  };

  const openTrackVersionEditor = (config: TrackVersionEditorConfig) => {
    // A BORROWED track's canonical `tracks-v2` document is origin-only, so
    // the watcher below would answer permission-denied instead of a track.
    // Nothing else ever puts that track in the store either (the legacy
    // space query cannot return a borrowed track), so feed the store the
    // tile itself — it carries the row's mirrored `versionFilesV2`, which is
    // what the editor renders.
    //
    // `isBorrowed`, never `isRowBacked`: the latter is true for a space's
    // OWN tracks as well (they have an origin row), and keying on it dropped
    // the live canonical watcher for all of them. The caller supplies it —
    // see the config type.
    const { isBorrowed } = config;
    setTrackId(config.track.id, config.viewingSpaceId);
    setBorrowedTrackId(isBorrowed ? config.track.id : undefined);
    if (isBorrowed) {
      // The store's map is `Map<string, Track>`, and a borrowed tile is a
      // `RenderableTrack`: it may have no canonical `versionFilesV2`/`files`
      // until the row's content mirror is backfilled. Putting the tile there
      // anyway IS the design (§5.5) — nothing else can ever fill that slot
      // for a borrowed track. The cast is about the store's element type,
      // which this branch cannot widen; it makes no claim about
      // `isBorrowed`, which is now typed.
      globalTracksDispatch({
        type: "SET_TRACK",
        payload: config.track as Track,
      });
    }
    if (config.type) {
      setTrackVersionEditorType(config.type);
    }
    if (config.type === "swap" && config.selectedVersionToSwap) {
      setSelectedVersionToSwap(config.selectedVersionToSwap);
    }
    setTrackVersionEditorOpen(true);
  };

  const closeTrackVersionEditor = () => {
    if (trackVersionEditorType) {
      setTrackVersionEditorType("default");
    }
    if (selectedVersionToSwap) {
      setSelectedVersionToSwap(undefined);
    }
    setTrackVersionEditorOpen(false);
  };

  // Watch the track when it's set
  useEffect(() => {
    if (!trackId) {
      resetTrackVersionEditor();
      return;
    }

    // See openTrackVersionEditor: a borrowed track's canonical document is
    // unreadable for this viewer, and its tile is already in the store.
    if (isBorrowedTrack) return;

    // Attach watcher for the track
    manageGlobalTrackWatchers({
      action: "attach",
      entityId: trackId,
      componentId: TRACK_VERSION_EDITOR_WATCHER_COMPONENT_ID,
      addEntity: (track) => {
        globalTracksDispatch({
          type: "SET_TRACK",
          payload: track,
        });
      },
      removeEntity: (entityId) => {
        if (entityId === trackId) {
          resetTrackVersionEditor();
        }
      },
    });

    return () => {
      // Cleanup watcher when component unmounts or track changes
      manageGlobalTrackWatchers({
        action: "detach",
        entityId: trackId,
        componentId: TRACK_VERSION_EDITOR_WATCHER_COMPONENT_ID,
        addEntity: () => {},
        removeEntity: () => {
          resetTrackVersionEditor();
        },
      });
    };
  }, [trackId, isBorrowedTrack]);

  useEffect(() => {
    if (trackVersionsLoading) {
      return;
    }

    // Only update versions if we have both track versions and pinned versions
    if (trackVersions?.length > 0) {
      if (!isEqual(trackVersions, localTrackVersions)) {
        setLocalTrackVersions(trackVersions);
      }
      if (!isEqual(globalTrack?.pinnedVersionFiles, localPinnedTrackVersions)) {
        setLocalPinnedTrackVersions(globalTrack?.pinnedVersionFiles || []);
      }
    } else {
      // If we have no track versions, reset both arrays
      setLocalTrackVersions([]);
      setLocalPinnedTrackVersions([]);
    }
  }, [trackVersions, globalTrack?.pinnedVersionFiles, trackVersionsLoading]);

  return (
    <TrackVersionEditorContext.Provider
      value={{
        ...versionRows,
        localTrackVersions,
        setLocalTrackVersions,
        localPinnedTrackVersions,
        setLocalPinnedTrackVersions,
        openTrackVersionEditor,
        closeTrackVersionEditor,
        isTrackVersionEditorOpen,
        trackVersionEditorType,
        selectedVersionToSwap,
        setSelectedVersionToSwap,
        setTrackId,
        viewingSpaceId,
        trackVersionsLoading,
      }}
    >
      {children}
      <FileEditorProvider>
        <EntitiesSelectionProvider>
          <TrackVersionSelectionProvider>
            {isTrackVersionEditorOpen && (
              <TrackVersionEditor
                track={globalTrack}
                onClose={closeTrackVersionEditor}
              />
            )}
          </TrackVersionSelectionProvider>
        </EntitiesSelectionProvider>
      </FileEditorProvider>
    </TrackVersionEditorContext.Provider>
  );
};

export const useTrackVersionEditorContext = () => {
  const context = useContext(TrackVersionEditorContext);
  if (!context) {
    throw new Error(
      "useTrackVersionEditorContext must be used within TrackVersionEditorProvider",
    );
  }
  return context;
};
